import { useRef, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  changeSource,
  type ControlledSource,
  getControlledSource,
} from "@/api/filth-source-control-api"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import {
  boundLibrariesError,
  sourceControlKey as key,
  sourceControlError,
} from "./filth-source-control-utils"

export function FilthRemoveSourceDialog({
  backendId,
  source,
  onClose,
  onSaved,
}: {
  backendId: string
  source: ControlledSource
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [confirmation, setConfirmation] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<{ id: string; name: string }[] | null>(null)
  const [busy, setBusy] = useState(false)
  const actionKey = useRef(crypto.randomUUID())
  const fresh = useQuery({
    queryKey: ["filth-remove", backendId, source.uid],
    queryFn: () => getControlledSource(backendId, source.uid),
    retry: false,
    staleTime: 0,
  })
  const current = fresh.data
  const libraries = blocked ?? current?.bound_libraries ?? source.bound_libraries
  async function remove() {
    if (!current || libraries.length || confirmation !== (current.name ?? current.uid)) return
    setBusy(true)
    setError(null)
    try {
      await changeSource(backendId, "delete", {
        body: { confirm_uid: current.uid },
        key: actionKey.current,
        source: current,
      })
      onSaved()
      onClose()
    } catch (e) {
      setError(sourceControlError(e))
      const bindings = boundLibrariesError(e)
      if (bindings.length) setBlocked(bindings)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`${key}.remove`)}</DialogTitle>
          <DialogDescription>{t(`${key}.removeWarning`)}</DialogDescription>
        </DialogHeader>
        {!!libraries.length && (
          <div className="space-y-2 text-sm" role="alert">
            <p>{t(`${key}.boundWarning`)}</p>
            <ul className="list-inside list-disc">
              {libraries.map((v) => (
                <li key={v.id}>{v.name}</li>
              ))}
            </ul>
          </div>
        )}
        <Label className="grid gap-2">
          {t(`${key}.typeName`, { name: current?.name ?? source.name ?? source.uid })}
          <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
        </Label>
        {(error || fresh.error) && (
          <p role="alert">{t(`${key}.errors.${error ?? sourceControlError(fresh.error)}`)}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button disabled={busy} variant="outline" onClick={onClose}>
            {t(`${key}.cancel`)}
          </Button>
          <Button
            className="border-foreground border"
            variant="destructive"
            disabled={
              busy ||
              fresh.isFetching ||
              !current ||
              !!libraries.length ||
              confirmation !== (current.name ?? current.uid)
            }
            onClick={() => void remove()}
          >
            {t(`${key}.remove`)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
